import { alpha, createTheme, Theme } from "@mui/material";

export const project = {
    name: "InsureScope",
};

export default project;

export const styles = {
    primaryColor: "#223876",
    primaryLight: "3b82f6",
    blue: "#2196F3",
    secondaryColor: "#F06723",
    tertiaryColor: "#D2E5FA",
    tertiaryLight: "#AACEF5",
    white: "#FFFFFF",
    backgroundColor: "#f5f6fa",
    successColor: "#2E7D32",
    successLight: "#E7F5E9",
    warningColor: "#faad14",
    warningLight: "#fef3c7",
    warningLightHover: "#fef3c7", // Same as warningLight - semantic alias for hover state
    warningDark: "#92400e",
    infoColor: "#1890ff",
    errorColor: "#AF0101",
    errorLight: "#fef2f2",
    errorBorder: "#fee2e2",
    thumbsDown: "#DF4402",
    thumbsUp: "#6FBF0B",
    buttonDark: "#4F4F4F",
    buttonDarkSharp: "#1E1E1E",
    buttonGray: "#757575",
    textDark: "#54595E",
    // Sidebar navigation colors
    sidenavHeaderText: "#94a3b8",
    sidenavText: "#475569",
    sidenavTextActive: "#3b82f6",
    sidenavBackgroundActive: "#eff6ff",
    sidenavBackgroundHover: "#f8fafc",
    sidenavTextHover: "#1a1f36",
    sidenavActiveIndicator: "#3b82f6",
};

// extend the Palette interface
// https://mui.com/material-ui/customization/palette/#typescript
declare module "@mui/material/styles" {
    interface Palette {
        tertiary: Palette["primary"];
        dark: Palette["primary"];
        darkSharp: Palette["primary"];
        gray: Palette["primary"];
        thumbsDownOn: Palette["primary"];
        thumbsDownOff: Palette["primary"];
        thumbsUpOn: Palette["primary"];
        thumbsUpOff: Palette["primary"];
        textDark: Palette["text"];
        successLight: Palette["primary"];
        sidenav: {
            headerText: string;
            text: string;
            textActive: string;
            backgroundActive: string;
            backgroundHover: string;
            textHover: string;
            activeIndicator: string;
        };
        ochre: Palette["primary"];
    }
    interface PaletteOptions {
        tertiary?: PaletteOptions["primary"];
        dark?: PaletteOptions["primary"];
        darkSharp?: PaletteOptions["primary"];
        gray?: PaletteOptions["primary"];
        thumbsDownOn?: PaletteOptions["primary"];
        thumbsDownOff?: PaletteOptions["primary"];
        thumbsUpOn: PaletteOptions["primary"];
        thumbsUpOff: PaletteOptions["primary"];
        textDark?: PaletteOptions["text"];
        successLight?: PaletteOptions["primary"];
        sidenav?: {
            headerText?: string;
            text?: string;
            textActive?: string;
            backgroundActive?: string;
            backgroundHover?: string;
            textHover?: string;
            activeIndicator?: string;
        };
        ochre: PaletteOptions["primary"];
    }
}

declare module "@mui/material/Button" {
    interface ButtonPropsColorOverrides {
        tertiary: true;
        dark: true;
        darkSharp: true;
        gray: true;
        thumbsDownOn: true;
        thumbsDownOff: true;
        thumbsUpOn: true;
        thumbsUpOff: true;
    }
}

declare module "@mui/material/IconButton" {
    interface IconButtonPropsColorOverrides {
        tertiary: true;
        dark: true;
        darkSharp: true;
        gray: true;
        thumbsDownOn: true;
        thumbsDownOff: true;
        thumbsUpOn: true;
        thumbsUpOff: true;
    }
}

declare module "@mui/material/Chip" {
    interface ChipPropsColorOverrides {
        tertiary: true;
        successLight: true;
    }
}

declare module "@mui/material/Typography" {
    interface TypographyPropsColorOverrides {
        textDark: true;
    }
}

declare module "@mui/material/LinearProgress" {
    interface LinearProgressPropsColorOverrides {
        tertiary: true;
    }
}

export const theme = createTheme({
    palette: {
        primary: { main: styles.primaryColor },
        secondary: {
            main: styles.secondaryColor,
            light: styles.tertiaryColor,
        },
        success: {
            main: styles.successColor,
        },
        successLight: {
            main: styles.successLight,
            contrastText: styles.successColor,
        },
        warning: {
            main: styles.warningColor,
        },
        info: {
            main: styles.infoColor,
        },
        error: {
            main: styles.errorColor,
        },
        background: {
            default: styles.backgroundColor,
        },
        tertiary: {
            main: styles.tertiaryColor,
            contrastText: styles.primaryColor,
        },
        dark: {
            main: styles.buttonDark,
            dark: alpha(styles.buttonDark, 0.55), // hover color
            light: alpha(styles.buttonDark, 0.35), // active color
            contrastText: styles.white,
        },
        darkSharp: {
            main: styles.buttonDarkSharp,
            dark: alpha(styles.buttonDarkSharp, 0.55), // hover color
            light: alpha(styles.buttonDarkSharp, 0.35), // active color
            contrastText: styles.white,
        },
        gray: {
            main: alpha(styles.buttonDark, 0.55),
            dark: alpha(styles.buttonDark, 0.9),
            light: alpha(styles.buttonDark, 0.2),
            contrastText: styles.white,
        },
        thumbsDownOn: {
            main: styles.thumbsDown,
            dark: styles.errorColor,
            contrastText: styles.white,
        },
        thumbsDownOff: {
            main: styles.white,
            contrastText: styles.thumbsDown,
            dark: "#E2E2E2",
        },
        thumbsUpOn: {
            main: styles.thumbsUp,
            dark: styles.successColor,
            contrastText: styles.white,
        },
        thumbsUpOff: {
            main: styles.white,
            contrastText: styles.thumbsUp,
            dark: "#E2E2E2",
        },
        textDark: {
            primary: styles.textDark,
            secondary: alpha(styles.textDark, 0.6),
        },
        sidenav: {
            headerText: styles.sidenavHeaderText,
            text: styles.sidenavText,
            textActive: styles.sidenavTextActive,
            backgroundActive: styles.sidenavBackgroundActive,
            backgroundHover: styles.sidenavBackgroundHover,
            textHover: styles.sidenavTextHover,
            activeIndicator: styles.sidenavActiveIndicator,
        },
        ochre: {
            main: "#E3D026",
            light: "#E9DB5D",
            dark: "#A29415",
            contrastText: "#242105",
        },
    },
    typography: {
        fontFamily: "var(--font-sans)",
    },
    shape: {
        borderRadius: 4,
    },
    shadows: (() => {
        const shadows = [
            "none",
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        ];
        return [...shadows, ...Array(19).fill("none")] as Theme["shadows"];
    })(),
    spacing: 8,
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                // CssBaseline paints the document from the MUI palette, in
                // `@layer mui` — above the `base` token rules it would
                // otherwise silently override. Point both at the same tokens.
                html: {
                    "&.dark": {
                        colorScheme: "dark",
                    },
                },
                body: {
                    backgroundColor: "var(--background)",
                    color: "var(--foreground)",
                },
                "body::backdrop": {
                    backgroundColor: "var(--background)",
                },
                "*::-webkit-scrollbar": {
                    width: "8px",
                    height: "8px",
                },
                "*::-webkit-scrollbar-track": {
                    background: "transparent",
                },
                "*::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0,0,0,0.2)",
                    borderRadius: "4px",
                },
                "*::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "rgba(0,0,0,0.3)",
                },
                // CssBaseline's own `strong, b` rule lands in `@layer mui`,
                // above the `elements` defaults in style.css, so bold weight
                // has to be restated here or MUI's 700 wins.
                "strong, b": {
                    fontWeight: "var(--font-weight-semibold)",
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: 8,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 4,
                    },
                },
            },
        },
    },
});
